import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Search, Radar, Users, Hash, Activity, Menu, X, LogOut } from "lucide-react";
import { DisclaimerFooter } from "@/components/Disclaimer";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/lib/auth";

export default function Layout({ children }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    const v = q.trim();
    if (!v) return;
    if (/^[A-Za-z]{1,5}$/.test(v)) {
      navigate(`/ticker/${v.toUpperCase()}`);
    } else {
      const slug = v.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z-]/g, "");
      navigate(`/expert/${slug}`);
    }
    setQ("");
  };

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  // Close mobile nav on route change
  useEffect(() => {
    const close = () => setMobileNavOpen(false);
    window.addEventListener("popstate", close);
    return () => window.removeEventListener("popstate", close);
  }, []);

  const navItem = ({ isActive }) =>
    `flex items-center gap-2.5 border-l-2 px-4 py-2.5 text-sm transition-colors ${
      isActive
        ? "border-[#3b82f6] bg-[#0f172a] text-[#f8fafc]"
        : "border-transparent text-[#94a3b8] hover:border-[#334155] hover:text-[#f8fafc]"
    }`;

  const NavItems = ({ onClick }) => (
    <>
      <div className="mb-2 px-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
        Feeds
      </div>
      <NavLink to="/feed" end className={navItem} data-testid="nav-convergence" onClick={onClick}>
        <Activity className="h-4 w-4" /> Convergence
      </NavLink>
      <NavLink to="/experts" className={navItem} data-testid="nav-experts" onClick={onClick}>
        <Users className="h-4 w-4" /> Experts
      </NavLink>

      <div className="mb-2 mt-6 px-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
        Shortcuts
      </div>
      {["NVDA", "MU", "MSTR", "TSLA", "COIN", "IBIT"].map((t) => (
        <NavLink
          key={t}
          to={`/ticker/${t}`}
          className={navItem}
          data-testid={`sidebar-ticker-${t}`}
          onClick={onClick}
        >
          <Hash className="h-4 w-4" />
          <span className="mono">{t}</span>
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-[#0b1120] text-[#f8fafc]">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 border-b border-[#1e293b] bg-[#0b1120]/95 backdrop-blur"
        data-testid="app-header"
      >
        <div
          className="mx-auto flex max-w-[1400px] items-center gap-3 pb-3 md:gap-6"
          style={{
            paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)",
            paddingLeft:
              "max(env(safe-area-inset-left, 0px), 1rem)",
            paddingRight:
              "max(env(safe-area-inset-right, 0px), 1rem)",
          }}
        >
          <button
            type="button"
            data-testid="mobile-nav-toggle"
            aria-label="Open navigation"
            onClick={() => setMobileNavOpen(true)}
            className="flex h-9 w-9 shrink-0 items-center justify-center border border-[#1e293b] text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc] md:hidden"
          >
            <Menu className="h-4 w-4" />
          </button>

          <Link
            to="/feed"
            data-testid="brand-link"
            className="flex shrink-0 items-center gap-2"
          >
            <div className="flex h-7 w-7 items-center justify-center border border-[#3b82f6]/40 bg-[#3b82f6]/10">
              <Radar className="h-4 w-4 text-[#3b82f6]" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight text-[#f8fafc]">
                SIGNAL EDGE
              </div>
              <div className="hidden font-mono text-[9px] uppercase tracking-[0.2em] text-[#64748b] sm:block">
                convergence intel
              </div>
            </div>
          </Link>

          <form onSubmit={onSubmit} className="min-w-0 flex-1">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
              <input
                data-testid="global-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search expert or ticker ..."
                className="w-full border border-[#1e293b] bg-[#111827] py-2 pl-10 pr-3 text-sm text-[#f8fafc] placeholder:text-[#64748b] focus:border-[#3b82f6] focus:outline-none focus:ring-1 focus:ring-[#3b82f6]"
              />
            </div>
          </form>

          <ThemeToggle />

          <div className="hidden shrink-0 items-center gap-2 md:flex">
            <div className="flex items-center gap-1.5 border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#94a3b8]">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#10b981] opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#10b981]" />
              </span>
              Live
            </div>
            {user && user !== false && (
              <>
                <Link
                  to="/account"
                  data-testid="user-chip"
                  className="max-w-[180px] truncate border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[10px] text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc]"
                  title={`${user.email} — click to manage account`}
                >
                  {user.email}
                </Link>
                <button
                  type="button"
                  onClick={onLogout}
                  data-testid="logout-button"
                  className="inline-flex items-center gap-1 border border-[#1e293b] bg-[#111827] px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc]"
                >
                  <LogOut className="h-3 w-3" /> Sign out
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px] gap-0">
        {/* Desktop sidebar */}
        <aside
          data-testid="sidebar-nav"
          className="hidden w-[220px] shrink-0 border-r border-[#1e293b] py-4 md:block"
        >
          <NavItems />
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1 px-4 py-5 md:px-6 md:py-6">
          {children}
        </main>
      </div>

      {/* Mobile nav drawer */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          data-testid="mobile-nav-drawer"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileNavOpen(false)}
          />
          <aside
            data-testid="mobile-nav-panel"
            className="absolute left-0 top-0 flex h-full w-64 flex-col border-r border-[#1e293b] bg-[#0b1120]"
            style={{
              paddingTop: "env(safe-area-inset-top, 0px)",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
              paddingLeft: "env(safe-area-inset-left, 0px)",
            }}
          >
            <div className="flex items-center justify-between border-b border-[#1e293b] px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center border border-[#3b82f6]/40 bg-[#3b82f6]/10">
                  <Radar className="h-4 w-4 text-[#3b82f6]" />
                </div>
                <div className="text-sm font-bold tracking-tight">SIGNAL EDGE</div>
              </div>
              <button
                type="button"
                data-testid="mobile-nav-close"
                aria-label="Close navigation"
                onClick={() => setMobileNavOpen(false)}
                className="flex h-8 w-8 items-center justify-center text-[#94a3b8] hover:text-[#f8fafc]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-4">
              <NavItems onClick={() => setMobileNavOpen(false)} />
            </div>
          </aside>
        </div>
      )}

      <DisclaimerFooter />
    </div>
  );
}
